import React, { useCallback, useContext } from 'react';
import {
  Popover,
  StyledPopover,
} from '@alefeducation/ui-elements-library-core';
import PropTypes from 'prop-types';
import {
  Download,
  RedoAlt,
  Upload,
} from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  getAttachmentGeneralFileType,
  getDownloadableFileLink,
  getFileLink,
} from '../AssignmentSubmission/attachment.utils';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import {
  useStudentSubmissionDispatch,
  useStudentSubmissionState,
} from './StudentSubmissionPageContext';

const AssignmentSubmissionActionsPopover = ({
  isActionsPopoverOpen,
  actionsPopoverButtonRef,
  attachment,
  fileNameSuffix,
  isReturned,
  attachmentRequired,
  isAssignmentResubmissionEnabled,
  closeHandler,
}) => {
  const isDocumentAttachment =
    attachment && getAttachmentGeneralFileType(attachment) === 'document';

  const isDownloadFileActionEnabled =
    attachmentRequired && isDocumentAttachment && attachment;
  const isUploadActionEnabled =
    attachmentRequired && isDocumentAttachment && !isReturned;
  return (
    <Popover
      anchorRef={actionsPopoverButtonRef}
      isOpen={isActionsPopoverOpen}
      popoverPosition="bottom-end"
      popoverOffset={5}
      style={{ width: '260px', height: '148px' }}
    >
      <StyledPopover>
        <PopOverActionsContainer
          data-testid="lesson-card-popover-container"
          onClick={e => {
            e.stopPropagation();
            closeHandler();
          }}
        >
          {isAssignmentResubmissionEnabled && <RequestResubmissionAction />}
          {isDownloadFileActionEnabled && (
            <DownloadFileAction
              attachment={attachment}
              fileNameSuffix={fileNameSuffix}
            />
          )}
          {isUploadActionEnabled && <UploadCorrectedFile />}
        </PopOverActionsContainer>
      </StyledPopover>
    </Popover>
  );
};

AssignmentSubmissionActionsPopover.defaultProps = {
  fileNameSuffix: '',
  isReturned: false,
};

AssignmentSubmissionActionsPopover.propTypes = {
  isActionsPopoverOpen: PropTypes.bool.isRequired,
  actionsPopoverButtonRef: PropTypes.node.isRequired,
  attachment: PropTypes.object.isRequired,
  fileNameSuffix: PropTypes.string,
  isReturned: PropTypes.bool,
  attachmentRequired: PropTypes.bool.isRequired,
  isAssignmentResubmissionEnabled: PropTypes.bool.isRequired,
  closeHandler: PropTypes.func.isRequired,
};
export default AssignmentSubmissionActionsPopover;

const RequestResubmissionAction = () => {
  const { t } = useTranslation();
  const dispatch = useStudentSubmissionDispatch();
  const { selectedStudent } = useStudentSubmissionState();
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();

  return (
    <ActionContainer
      onClick={() =>
        dispatch({
          type: 'openRequestResubmissionModal',
          payload: {
            studentId: selectedStudent.student.id,
          },
        })
      }
      className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-request-resubmission`}
    >
      <RedoAlt fontSize={18} />
      <ActionText>{t('Request Resubmission')}</ActionText>
    </ActionContainer>
  );
};

const DownloadFileAction = ({ attachment, fileNameSuffix }) => {
  const { t } = useTranslation();
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const downloadFileHandler = useCallback(() => {
    const url = getDownloadableFileLink(
      getFileLink(attachment),
      attachment.fileName,
      fileNameSuffix,
    );
    window.open(url, '_blank');
  });
  return (
    <ActionContainer
      data-testid="student-attachment-download-button"
      onClick={downloadFileHandler}
      className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-download-file`}
    >
      <Download fontSize={18} />
      <ActionText>{t('Download File')}</ActionText>
    </ActionContainer>
  );
};

DownloadFileAction.defaultProps = {
  fileNameSuffix: '',
};

DownloadFileAction.propTypes = {
  attachment: PropTypes.object.isRequired,
  fileNameSuffix: PropTypes.string,
};

const UploadCorrectedFile = () => {
  const dispatch = useStudentSubmissionDispatch();
  const { t } = useTranslation();
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  return (
    <ActionContainer
      onClick={() =>
        dispatch({
          type: 'openFileUploadConfirmationModal',
        })
      }
      className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-upload-corrected-file`}
      data-testid={'student-attachment-upload-button'}
    >
      <Upload fontSize={18} />
      <ActionText>{t('Upload Corrected File')}</ActionText>
    </ActionContainer>
  );
};

const PopOverActionsContainer = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0px 22px 54px rgba(72, 84, 101, 0.2);
  border-radius: 10px;
  border: 1px #dadde0 solid;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  padding: 12px 16px 12px 24px;
  width: 100%;
  height: 44px;
  background: transparent;
  justify-content: flex-start !important;
  border-radius: 8px;

  &:hover {
    background-color: var(--option-hover);
  }
`;
const ActionText = styled.div`
  color: var(--river-bed);
  font-size: 16px;
  font-family: var(--font-family-signika);
  font-weight: 300;
  word-wrap: break-word;
`;
